// WP-3.4 (M4 SC-1..8, S-14 `/expected`): Toggle List/Rollups/Aging (Calendar is a documented
// scope trim — a full calendar UI is a separate component, the same "surfaced, not faked"
// discipline this session applies to every out-of-scope surface). Rollup chips 7d/30d/month/
// year/overdue/undated split C/L/T (SC-3/4); list rows carry party/project/amount(expected vs
// remaining)/date-or-undated/confidence chip/status; Receive pre-links an income transaction
// (partial allowed, SC-5); Aging renders SC-7's buckets.
import {
  formatMoney,
  type ConfidenceLevel,
  type CurrencyCode,
  type ScheduleItemStatus,
} from '@pfos/engine';
import type { ScheduleItemResponse } from '@pfos/shared';
import { CalendarClock, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Amount } from '../components/Amount';
import { EmptyState } from '../components/EmptyState';
import {
  AmountField,
  Field,
  ghostButtonInlineClass,
  inputClass,
  primaryButtonInlineClass,
  selectClass,
} from '../components/FormKit';
import { FormSheet } from '../components/FormSheet';
import { PartyPicker } from '../components/PartyPicker';
import { TransactionForm } from '../components/TransactionForm';
import { useAccounts } from '../lib/accounts';
import { useParties } from '../lib/parties';
import { useProjects } from '../lib/projects';
import {
  useAgingBuckets,
  useCancelScheduleItem,
  useCreateScheduleItem,
  useRescheduleScheduleItem,
  useScheduleItems,
  useScheduleRollups,
} from '../lib/scheduleItems';

const VIEWS = ['list', 'rollups', 'aging'] as const;
type View = (typeof VIEWS)[number];

const STATUS_LABELS: Record<ScheduleItemStatus, string> = {
  upcoming: 'Upcoming',
  due: 'Due',
  partially_paid: 'Partially paid',
  paid: 'Paid',
  overdue: 'Overdue',
  cancelled: 'Cancelled',
};

const STATUS_CLASSES: Record<ScheduleItemStatus, string> = {
  upcoming: 'text-muted',
  due: 'text-warning',
  partially_paid: 'text-warning',
  paid: 'text-success',
  overdue: 'text-danger',
  cancelled: 'text-muted',
};

function CurrencyPicker({
  currency,
  onChange,
}: {
  currency: CurrencyCode;
  onChange: (c: CurrencyCode) => void;
}): React.JSX.Element {
  return (
    <select
      className={`${selectClass} w-auto`}
      aria-label="Currency"
      value={currency}
      onChange={(e) => {
        onChange(e.target.value as CurrencyCode);
      }}
    >
      {['PKR', 'USD', 'EUR', 'GBP'].map((c) => (
        <option key={c} value={c}>
          {c}
        </option>
      ))}
    </select>
  );
}

function RescheduleForm({
  item,
  onDone,
}: {
  item: ScheduleItemResponse;
  onDone: () => void;
}): React.JSX.Element {
  const [date, setDate] = useState(item.expectedDate?.slice(0, 10) ?? '');
  const rescheduleMutation = useRescheduleScheduleItem(item.id);
  return (
    <div className="mt-2 flex items-end gap-2 rounded-[var(--radius-control)] border border-border bg-bg p-2">
      <Field label="New date (blank = undated)" htmlFor={`reschedule-date-${item.id}`}>
        <input
          id={`reschedule-date-${item.id}`}
          type="date"
          className={inputClass}
          value={date}
          onChange={(e) => {
            setDate(e.target.value);
          }}
        />
      </Field>
      <button
        type="button"
        className={primaryButtonInlineClass}
        disabled={rescheduleMutation.isPending}
        onClick={() => {
          rescheduleMutation.mutate(
            { newDate: date === '' ? null : new Date(date).toISOString() },
            { onSuccess: onDone },
          );
        }}
      >
        Save
      </button>
      <button type="button" className={ghostButtonInlineClass} onClick={onDone}>
        Cancel
      </button>
    </div>
  );
}

function CancelForm({
  item,
  onDone,
}: {
  item: ScheduleItemResponse;
  onDone: () => void;
}): React.JSX.Element {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const cancelMutation = useCancelScheduleItem(item.id);
  return (
    <div className="mt-2 flex items-end gap-2 rounded-[var(--radius-control)] border border-border bg-bg p-2">
      {error !== null && <p className="text-sm text-danger">{error}</p>}
      <Field label="Cancellation reason (required)" htmlFor={`cancel-reason-${item.id}`}>
        <input
          id={`cancel-reason-${item.id}`}
          className={inputClass}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
          }}
        />
      </Field>
      <button
        type="button"
        className={primaryButtonInlineClass}
        disabled={cancelMutation.isPending}
        onClick={() => {
          if (reason.trim() === '') {
            setError('A reason is required');
            return;
          }
          cancelMutation.mutate({ reason: reason.trim() }, { onSuccess: onDone });
        }}
      >
        Confirm cancel
      </button>
      <button type="button" className={ghostButtonInlineClass} onClick={onDone}>
        Back
      </button>
    </div>
  );
}

function ScheduleItemRow({ item }: { item: ScheduleItemResponse }): React.JSX.Element {
  const [action, setAction] = useState<'none' | 'reschedule' | 'cancel'>('none');
  const [receiveOpen, setReceiveOpen] = useState(false);
  const canReceive = item.status !== 'paid' && item.status !== 'cancelled';
  const canReschedule = item.status !== 'cancelled';
  const canCancel = item.status !== 'paid' && item.status !== 'cancelled';

  return (
    <li className="rounded-[var(--radius-card)] border border-border bg-surface p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-ink">
            {item.party}
            {item.projectId !== undefined && <span className="text-muted"> · project-linked</span>}
          </p>
          <p className="text-xs text-muted">
            {formatMoney({ currency: item.currency, minorUnits: item.expectedAmountMinor })}{' '}
            expected
            {item.remainingMinor !== item.expectedAmountMinor && (
              <>
                {' '}
                — {formatMoney({ currency: item.currency, minorUnits: item.remainingMinor })}{' '}
                remaining
              </>
            )}
            {' · '}
            {item.expectedDate !== undefined
              ? new Date(item.expectedDate).toLocaleDateString()
              : 'undated'}
            {item.milestone !== undefined && <> · {item.milestone}</>}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="rounded-full border border-border px-2 py-0.5 text-xs capitalize text-muted">
            {item.confidence}
          </span>
          <span className={`text-xs font-medium ${STATUS_CLASSES[item.status]}`}>
            {STATUS_LABELS[item.status]}
          </span>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {canReceive && (
          <button
            type="button"
            className={ghostButtonInlineClass}
            onClick={() => {
              setReceiveOpen(true);
            }}
          >
            Receive
          </button>
        )}
        {canReschedule && (
          <button
            type="button"
            className={ghostButtonInlineClass}
            onClick={() => {
              setAction(action === 'reschedule' ? 'none' : 'reschedule');
            }}
          >
            Reschedule
          </button>
        )}
        {canCancel && (
          <button
            type="button"
            className={ghostButtonInlineClass}
            onClick={() => {
              setAction(action === 'cancel' ? 'none' : 'cancel');
            }}
          >
            Cancel
          </button>
        )}
      </div>
      <TransactionForm
        open={receiveOpen}
        onOpenChange={setReceiveOpen}
        initialType="income"
        initialParty={item.party}
        {...(item.partyId !== undefined ? { initialPartyId: item.partyId } : {})}
        initialScheduleItemId={item.id}
      />
      {action === 'reschedule' && (
        <RescheduleForm
          item={item}
          onDone={() => {
            setAction('none');
          }}
        />
      )}
      {action === 'cancel' && (
        <CancelForm
          item={item}
          onDone={() => {
            setAction('none');
          }}
        />
      )}
    </li>
  );
}

function NewScheduleItemForm({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.JSX.Element {
  const [party, setParty] = useState('');
  const [partyId, setPartyId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [currency, setCurrency] = useState<CurrencyCode>('PKR');
  const [amount, setAmount] = useState('');
  const [expectedDate, setExpectedDate] = useState('');
  const [milestone, setMilestone] = useState('');
  const [confidence, setConfidence] = useState<ConfidenceLevel>('confirmed');
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateScheduleItem();
  const { data: parties } = useParties(false);
  const { data: projects } = useProjects();

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title="New schedule item"
      submitLabel="Save"
      submitting={createMutation.isPending}
      onSubmit={() => {
        setError(null);
        if (party.trim() === '') {
          setError('Party is required');
          return;
        }
        const expectedAmountMinor = Math.round(Number(amount) * 100);
        if (!(expectedAmountMinor > 0)) {
          setError('Amount must be a positive number');
          return;
        }
        createMutation.mutate(
          {
            party: party.trim(),
            ...(partyId !== '' ? { partyId } : {}),
            ...(projectId !== '' ? { projectId } : {}),
            currency,
            expectedAmountMinor,
            ...(expectedDate !== '' ? { expectedDate: new Date(expectedDate).toISOString() } : {}),
            ...(milestone.trim() !== '' ? { milestone: milestone.trim() } : {}),
            confidence,
          },
          {
            onSuccess: () => {
              onOpenChange(false);
              setParty('');
              setPartyId('');
              setProjectId('');
              setAmount('');
              setExpectedDate('');
              setMilestone('');
              setConfidence('confirmed');
            },
            onError: (e) => {
              setError(e.message);
            },
          },
        );
      }}
    >
      {error !== null && <p className="mb-3 text-sm text-danger">{error}</p>}
      <Field label="Party (optional link)" htmlFor="si-party-id">
        <PartyPicker
          id="si-party-id"
          value={partyId}
          onChange={(id) => {
            setPartyId(id);
            const matched = (parties ?? []).find((p) => p.id === id);
            if (matched !== undefined) setParty(matched.name);
          }}
        />
      </Field>
      <Field label="Party name" htmlFor="si-party">
        <input
          id="si-party"
          className={inputClass}
          value={party}
          onChange={(e) => {
            setParty(e.target.value);
          }}
        />
      </Field>
      <Field label="Project (optional)" htmlFor="si-project">
        <select
          id="si-project"
          className={selectClass}
          value={projectId}
          onChange={(e) => {
            setProjectId(e.target.value);
          }}
        >
          <option value="">No project</option>
          {(projects ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.title}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Currency" htmlFor="si-currency">
        <select
          id="si-currency"
          className={selectClass}
          value={currency}
          onChange={(e) => {
            setCurrency(e.target.value as CurrencyCode);
          }}
        >
          {['PKR', 'USD', 'EUR', 'GBP'].map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Expected amount" htmlFor="si-amount">
        <AmountField id="si-amount" currency={currency} value={amount} onChange={setAmount} />
      </Field>
      <Field label="Expected date (optional — undated is a real bucket, SC-4)" htmlFor="si-date">
        <input
          id="si-date"
          type="date"
          className={inputClass}
          value={expectedDate}
          onChange={(e) => {
            setExpectedDate(e.target.value);
          }}
        />
      </Field>
      <Field label="Milestone note (optional)" htmlFor="si-milestone">
        <input
          id="si-milestone"
          className={inputClass}
          value={milestone}
          onChange={(e) => {
            setMilestone(e.target.value);
          }}
        />
      </Field>
      <Field label="Confidence" htmlFor="si-confidence">
        <select
          id="si-confidence"
          className={selectClass}
          value={confidence}
          onChange={(e) => {
            setConfidence(e.target.value as ConfidenceLevel);
          }}
        >
          <option value="confirmed">Confirmed</option>
          <option value="likely">Likely</option>
          <option value="tentative">Tentative</option>
        </select>
      </Field>
    </FormSheet>
  );
}

function RollupCard({
  label,
  split,
  currency,
}: {
  label: string;
  split: { confirmedMinor: number; likelyMinor: number; tentativeMinor: number };
  currency: CurrencyCode;
}): React.JSX.Element {
  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-surface p-3">
      <p className="mb-1 text-xs font-semibold uppercase text-muted">{label}</p>
      <p className="text-sm text-ink">
        <Amount minorUnits={split.confirmedMinor} currency={currency} /> C
      </p>
      <p className="text-sm text-muted">
        <Amount minorUnits={split.likelyMinor} currency={currency} /> L
      </p>
      <p className="text-sm text-muted">
        <Amount minorUnits={split.tentativeMinor} currency={currency} /> T
      </p>
    </div>
  );
}

export function ExpectedPage(): React.JSX.Element {
  const [view, setView] = useState<View>('list');
  const [formOpen, setFormOpen] = useState(false);
  const { data: accounts } = useAccounts();
  const [currency, setCurrency] = useState<CurrencyCode>('PKR');
  const currencyInUse =
    accounts?.find((a) => a.currency === currency) !== undefined
      ? currency
      : (accounts?.[0]?.currency ?? currency);

  const { data: items, isLoading } = useScheduleItems();
  const { data: rollups } = useScheduleRollups(currencyInUse);
  const { data: aging } = useAgingBuckets(currencyInUse);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold text-ink">Expected</h1>
        <button
          type="button"
          className="flex items-center gap-1 rounded-[var(--radius-control)] bg-primary px-3 py-1.5 text-sm font-medium text-primary-ink hover:opacity-90 active:opacity-80"
          onClick={() => {
            setFormOpen(true);
          }}
        >
          <Plus size={16} /> New schedule item
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex gap-1 rounded-[var(--radius-control)] border border-border p-1">
          {VIEWS.map((v) => (
            <button
              key={v}
              type="button"
              className={`rounded-[var(--radius-control)] px-3 py-1 text-sm capitalize ${view === v ? 'bg-primary text-primary-ink' : 'text-muted hover:text-ink'}`}
              onClick={() => {
                setView(v);
              }}
            >
              {v}
            </button>
          ))}
        </div>
        {view !== 'list' && <CurrencyPicker currency={currencyInUse} onChange={setCurrency} />}
      </div>

      {view === 'list' && (
        <>
          {isLoading && <p className="text-sm text-muted">Loading…</p>}
          {!isLoading && (items === undefined || items.length === 0) && (
            <EmptyState
              icon={CalendarClock}
              message="No schedule items yet."
              action={{
                label: 'New schedule item',
                onClick: () => {
                  setFormOpen(true);
                },
              }}
            />
          )}
          {items !== undefined && items.length > 0 && (
            <ul className="space-y-2">
              {items.map((item) => (
                <ScheduleItemRow key={item.id} item={item} />
              ))}
            </ul>
          )}
        </>
      )}

      {view === 'rollups' && rollups !== undefined && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <RollupCard label="Next 7 days" split={rollups.next7Days} currency={currencyInUse} />
          <RollupCard label="Next 30 days" split={rollups.next30Days} currency={currencyInUse} />
          <RollupCard label="This month" split={rollups.thisMonth} currency={currencyInUse} />
          <RollupCard label="This year" split={rollups.thisYear} currency={currencyInUse} />
          <RollupCard label="Overdue" split={rollups.overdue} currency={currencyInUse} />
          <RollupCard label="Undated" split={rollups.undated} currency={currencyInUse} />
        </div>
      )}

      {view === 'aging' && aging !== undefined && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { label: '1–30 days', bucket: aging.days1to30 },
            { label: '31–60 days', bucket: aging.days31to60 },
            { label: '61–90 days', bucket: aging.days61to90 },
            { label: '90+ days', bucket: aging.days90plus },
          ].map((b) => (
            <div
              key={b.label}
              className="rounded-[var(--radius-card)] border border-border bg-surface p-3"
            >
              <p className="mb-1 text-xs font-semibold uppercase text-muted">{b.label}</p>
              <p className="text-sm font-medium text-ink">
                <Amount minorUnits={b.bucket.amountMinor} currency={currencyInUse} />
              </p>
              <p className="text-xs text-muted">{b.bucket.count} item(s)</p>
            </div>
          ))}
        </div>
      )}

      <p className="mt-6 text-xs text-muted">
        See also{' '}
        <Link to="/owed" className="text-primary hover:underline">
          Owed
        </Link>{' '}
        for standalone receivables — SC-8 unifies both into the dashboard&apos;s own &quot;owed to
        me&quot; figure (Confirmed schedule items only).
      </p>

      <NewScheduleItemForm open={formOpen} onOpenChange={setFormOpen} />
    </div>
  );
}
